// Prototype state machines. Documented in docs/ff01/FF01_STATE_MACHINES.md.
// These define ALLOWED forward transitions for each tracked workflow type
// and are the single source of truth the store consults before mutating a
// canonical entity's status. Not a production contract — see
// docs/ff01/FF01_BACKEND_CONTRACT_NOTES.md for what a backend must formalize.

export type MachineKey = "work-order" | "tour" | "move-in" | "coi" | "support-request" | "move-out";

export interface MachineDefinition<TState extends string> {
  key: MachineKey;
  states: TState[];
  labels: Record<TState, string>;
  /** Allowed forward transitions. Some machines allow a branch (e.g. tour outcome). */
  transitions: Record<TState, TState[]>;
}

export function canTransition<TState extends string>(
  machine: MachineDefinition<TState>,
  from: TState,
  to: TState
): boolean {
  return machine.transitions[from]?.includes(to) ?? false;
}

export function stepStatus<TState extends string>(
  machine: MachineDefinition<TState>,
  current: TState
) {
  const currentIndex = machine.states.indexOf(current);
  return machine.states.map((state, index) => ({
    key: state,
    label: machine.labels[state],
    done: index < currentIndex,
    current: index === currentIndex
  }));
}

// --- Work Order ------------------------------------------------------------
export type WorkOrderState = "reported" | "assigned" | "in-progress" | "ready-to-verify" | "verified" | "closed";
export const workOrderMachine: MachineDefinition<WorkOrderState> = {
  key: "work-order",
  states: ["reported", "assigned", "in-progress", "ready-to-verify", "verified", "closed"],
  labels: {
    reported: "Reported",
    assigned: "Assigned",
    "in-progress": "In Progress",
    "ready-to-verify": "Ready to Verify",
    verified: "Verified",
    closed: "Closed"
  },
  transitions: {
    reported: ["assigned"],
    assigned: ["in-progress"],
    "in-progress": ["ready-to-verify"],
    "ready-to-verify": ["verified", "in-progress"], // failed verification sends it back
    verified: ["closed"],
    closed: []
  }
};

// --- Tour --------------------------------------------------------------
export type TourState = "scheduled" | "prep" | "touring" | "outcome-recorded" | "follow-up" | "closed-won" | "closed-lost";
export const tourMachine: MachineDefinition<TourState> = {
  key: "tour",
  states: ["scheduled", "prep", "touring", "outcome-recorded", "follow-up", "closed-won", "closed-lost"],
  labels: {
    scheduled: "Scheduled",
    prep: "Prep",
    touring: "Tour",
    "outcome-recorded": "Outcome",
    "follow-up": "Follow-Up",
    "closed-won": "Closed Won",
    "closed-lost": "Closed Lost"
  },
  transitions: {
    scheduled: ["prep"],
    prep: ["touring"],
    touring: ["outcome-recorded"],
    "outcome-recorded": ["follow-up", "closed-won", "closed-lost"],
    "follow-up": ["closed-won", "closed-lost"],
    "closed-won": [],
    "closed-lost": []
  }
};

// --- Move-In -----------------------------------------------------------
export type MoveInState = "lease-signed" | "readiness" | "walkthrough" | "ready" | "moved-in";
export const moveInMachine: MachineDefinition<MoveInState> = {
  key: "move-in",
  states: ["lease-signed", "readiness", "walkthrough", "ready", "moved-in"],
  labels: {
    "lease-signed": "Lease Signed",
    readiness: "Readiness",
    walkthrough: "Walkthrough",
    ready: "Ready",
    "moved-in": "Move-In"
  },
  transitions: {
    "lease-signed": ["readiness"],
    readiness: ["walkthrough"],
    walkthrough: ["ready"],
    ready: ["moved-in"],
    "moved-in": []
  }
};

// --- COI -----------------------------------------------------------------
export type COIState = "flagged" | "notified" | "received" | "verified";
export const coiMachine: MachineDefinition<COIState> = {
  key: "coi",
  states: ["flagged", "notified", "received", "verified"],
  labels: { flagged: "Flagged", notified: "Notified", received: "Received", verified: "Verified" },
  transitions: {
    flagged: ["notified"],
    notified: ["received"],
    received: ["verified"],
    verified: []
  }
};

// --- Support Request -------------------------------------------------------
export type SupportRequestState = "submitted" | "acknowledged" | "in-progress" | "waiting" | "resolved";
export const supportRequestMachine: MachineDefinition<SupportRequestState> = {
  key: "support-request",
  states: ["submitted", "acknowledged", "in-progress", "waiting", "resolved"],
  labels: {
    submitted: "Submitted",
    acknowledged: "Acknowledged",
    "in-progress": "In Progress",
    waiting: "Waiting",
    resolved: "Resolved"
  },
  transitions: {
    submitted: ["acknowledged"],
    acknowledged: ["in-progress"],
    "in-progress": ["waiting", "resolved"],
    waiting: ["in-progress", "resolved"],
    resolved: []
  }
};

// --- Move-Out / Turnover -----------------------------------------------
export type MoveOutState = "notice" | "pre-walk" | "move-out-inspection" | "turnover-actions" | "ready";
export const moveOutMachine: MachineDefinition<MoveOutState> = {
  key: "move-out",
  states: ["notice", "pre-walk", "move-out-inspection", "turnover-actions", "ready"],
  labels: {
    notice: "Notice",
    "pre-walk": "Pre-Walk",
    "move-out-inspection": "Move-Out Inspection",
    "turnover-actions": "Turnover Actions",
    ready: "Ready"
  },
  transitions: {
    notice: ["pre-walk"],
    "pre-walk": ["move-out-inspection"],
    "move-out-inspection": ["turnover-actions"],
    "turnover-actions": ["ready"],
    ready: []
  }
};
